import * as zExt from "@redotech/rpc/ext";
import * as z from "zod";

/** Mirrors the IMS selection limits so the Admin rejects oversize requests early. */
export const REFULFILLMENT_PUSH_MAX_LOCATIONS = 10;
export const REFULFILLMENT_PUSH_MAX_PRODUCTS = 200;

const DecimalId = z.string().regex(/^\d+$/);

const RefulfillmentMaxQuantity_ = z.object({
  productId: DecimalId,
  /** Whole, nonnegative cap on the quantity published at each location. */
  maxQuantity: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER),
});
export const RefulfillmentMaxQuantity: z.ZodType<RefulfillmentMaxQuantity> =
  RefulfillmentMaxQuantity_;
export interface RefulfillmentMaxQuantity extends z.infer<
  typeof RefulfillmentMaxQuantity_
> {}

const RefulfillmentInventoryPushRequest_ = z
  .object({
    merchantTeamId: zExt.objectId(),
    sourceLocationIds: z
      .array(DecimalId)
      .min(1)
      .max(REFULFILLMENT_PUSH_MAX_LOCATIONS),
    /** Absent means all inventory at the selected locations, with no caps. */
    productIds: z
      .array(DecimalId)
      .min(1)
      .max(REFULFILLMENT_PUSH_MAX_PRODUCTS)
      .optional(),
    maxQuantities: z
      .array(RefulfillmentMaxQuantity_)
      .max(REFULFILLMENT_PUSH_MAX_PRODUCTS)
      .optional(),
  })
  .superRefine((request, ctx) => {
    if (
      new Set(request.sourceLocationIds).size !==
      request.sourceLocationIds.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["sourceLocationIds"],
        message: "Locations must be unique",
      });
    }
    if (
      request.productIds &&
      new Set(request.productIds).size !== request.productIds.length
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["productIds"],
        message: "Products must be unique",
      });
    }
    const capped = (request.maxQuantities ?? []).map((cap) => cap.productId);
    const selected = new Set(request.productIds ?? []);
    if (
      new Set(capped).size !== capped.length ||
      capped.some((productId) => !selected.has(productId))
    ) {
      ctx.addIssue({
        code: "custom",
        path: ["maxQuantities"],
        message: "Each max quantity must belong to one selected product",
      });
    }
  });
export const RefulfillmentInventoryPushRequest: z.ZodType<
  RefulfillmentInventoryPushRequest,
  z.input<typeof RefulfillmentInventoryPushRequest_>
> = RefulfillmentInventoryPushRequest_;
export interface RefulfillmentInventoryPushRequest extends z.infer<
  typeof RefulfillmentInventoryPushRequest_
> {}
